import { listEpisodeDiagnostics, type EpisodeDiagnosticRowResp } from "@/api";
import { buildDiagnosticIndex, type EpisodeDiagnosticIndex } from "./episodeDiagnosticValues";

/**
 * 데이터셋의 진단 행을 모두 적재한다.
 *
 * `/diagnostics/episodes` 에는 에피소드 uuid 로 좁히는 파라미터가 없다. 그래서 단건을 가져올 수
 * 없다. 또 백분위는 전체 분포가 있어야 옳다. 그래서 상한이나 표본 절단 없이 전부 받는다.
 * 대신 진행률을 전달해 소비부가 대기 이유를 설명할 수 있게 한다.
 *
 * `api/page.ts::fetchAllPages` 를 쓰지 않는 이유는 다음과 같다. 그쪽에도 동시 요청 풀이 있다.
 * 그러나 이 적재기는 **abort 와 진행률**을 함께 소유해야 한다. 이 축은 20,000 건(200 페이지)까지
 * 커질 수 있다. 사용자가 화면을 떠나면 남은 페이지 요청을 끊어야 한다. 기다리는 동안에는 진행
 * 위치를 보여야 한다. `fetchAllPages` 는 순서를 보존한 배열만 돌려주고 이 둘을 지원하지 않는다.
 */

// BE `PAGE_SIZE_MAX` 와 같은 상한. 더 늘릴 수 없으므로 페이지 수가 곧 요청 수다.
const PAGE_SIZE = 100;

// 동시 페이지 요청 수. 교차 리전 RTT 가 200–300ms 다. 직렬이면 페이지 수만큼 왕복이 쌓인다.
// 무제한이면 큰 데이터셋에서 수백 요청을 한꺼번에 보낸다. 통상 규모(≤500 에피소드 = 5 페이지)는
// 한 라운드에 끝난다. 20,000 건(200 페이지)도 ~34 라운드를 넘지 않는다.
const PAGE_CONCURRENCY = 6;

export type EpisodeDiagnosticsProgress = {
  loaded: number;
  total: number;
};

export type LoadEpisodeDiagnosticsOptions = {
  signal: AbortSignal;
  onProgress: (progress: EpisodeDiagnosticsProgress) => void;
};

export type EpisodeDiagnosticsLoadResult = {
  index: EpisodeDiagnosticIndex;
  total: number;
};

/**
 * 진단 행을 전량 적재해 에피소드 uuid 색인으로 만든다.
 *
 * 첫 페이지로 `total` 을 확인한 뒤 나머지 페이지를 동시성 풀로 모두 가져온다. abort 되면
 * AbortError 를 던진다. 그 외 오류(권한·서버)는 그대로 전파한다. 부분 표본으로 만든 백분위는
 * 사실과 다르다. 그래서 부분 결과를 알리지 않고 이어붙이지 않는다.
 */
export async function loadEpisodeDiagnostics(
  datasetUuid: string,
  { signal, onProgress }: LoadEpisodeDiagnosticsOptions,
): Promise<EpisodeDiagnosticsLoadResult> {
  throwIfAborted(signal);
  const firstPage = await listEpisodeDiagnostics(datasetUuid, { page: 1, size: PAGE_SIZE });
  throwIfAborted(signal);

  const rows: EpisodeDiagnosticRowResp[] = [...firstPage.items];
  const total = firstPage.total;
  onProgress({ loaded: rows.length, total });

  // 다음 경우에는 더 요청하지 않는다.
  //   - 첫 페이지가 total 을 모두 담았다.
  //   - total 이 남았는데 받은 행이 없다. 서버가 빈 페이지를 계속 줄 때 무한 요청을 막는다.
  if (rows.length >= total || rows.length === 0) {
    return { index: buildDiagnosticIndex(rows), total };
  }

  const pageCount = Math.ceil(total / PAGE_SIZE);
  let nextPage = 2;
  const workerCount = Math.min(PAGE_CONCURRENCY, pageCount - 1);
  const workers = Array.from({ length: workerCount }, async () => {
    for (;;) {
      throwIfAborted(signal);
      const page = nextPage;
      nextPage += 1;
      if (page > pageCount) {
        return;
      }
      const resp = await listEpisodeDiagnostics(datasetUuid, { page, size: PAGE_SIZE });
      throwIfAborted(signal);
      rows.push(...resp.items);
      onProgress({ loaded: rows.length, total });
    }
  });
  await Promise.all(workers);

  return { index: buildDiagnosticIndex(rows), total };
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException("Episode diagnostics load aborted", "AbortError");
  }
}
